import { fetcherWithToken, sendRequest } from '@/shared/api/fetcher';
import useSWRMutation from 'swr/mutation';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';
import { useLoyaltyList } from '@/flat-pages/status/model/hooks';
import { useBonuses } from '@/features/profile/model/hooks';

function useAuthorization() {
  const { removeQueryParam } = useConfirmDataPrompt();
  const { mutateLoyaltyList } = useLoyaltyList();
  const { mutateBonuses } = useBonuses();

  const router = useRouter();
  const { setUser } = useUser();

  const handleLoginSuccess = async (token, expired, options = {}) => {
    const { shouldSetCookie = true, skipRedirect = false } = options;

    if (shouldSetCookie) {
      const days = expired / (24 * 60 * 60);
      Cookies.set('sessionId', token, { expires: days });
    }

    const [wishlist, profileData] = await Promise.all([
      triggers.getWishList(token),
      triggers.getProfile(token),
      mutateLoyaltyList(),
      mutateBonuses(),
    ]);

    setUser({
      ...profileData,
      wishlist,
      isAuth: true,
      isUserLoading: false,
    });

    if (!skipRedirect) {
      router.push('/');
    }
  };

  const createTrigger = (endpoint, useToken = false) =>
    useSWRMutation(endpoint, useToken ? fetcherWithToken : sendRequest).trigger;

  const triggers = {
    loginWithEmail: createTrigger('/login/email'),
    loginWithToken: createTrigger('/login/token'),
    loginWithPhone: createTrigger('/login/phone'),
    registerWithPhone: createTrigger('/register/phone'),
    registerWithEmail: createTrigger('/register/email'),
    confirmRegisterWithPhone: createTrigger('/register/phone/confirmation'),
    confirmRegisterWithEmail: createTrigger('/register/email/confirmation'),
    recoveryPasswordWithEmail: createTrigger('/recover/email'),
    recoveryPasswordWithPhone: createTrigger('/recover/phone'),
    createPasswordWithEmail: createTrigger('/recover'),
    getProfile: createTrigger('/profile/getProfileData', true),
    getWishList: createTrigger('/favorites', true),
    register: createTrigger('/register'),
  };

  const authenticate = async (values, stateKey) => {
    try {
      const response = await triggers[stateKey](values);

      if (response.error && stateKey !== 'loginWithToken') {
        return { success: false };
      }

      if (stateKey.startsWith('login')) {
        await handleLoginSuccess(response.token, response.expired);
      } else {
        router.push('/');
      }

      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const resendRequest = async (trigger, data) => {
    try {
      const response = await trigger(data);
      return response.error ? { success: false } : { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const emailCodeAuthenticate = async (code, fromProfile = false) => {
    if (!fromProfile) {
      setUser({ isUserLoading: true });
    }

    try {
      const response = await triggers.confirmRegisterWithEmail({ code });
      if (response.error) return { success: false };

      await handleLoginSuccess(response.token, response.expired, {
        shouldSetCookie: !fromProfile,
        skipRedirect: true,
      });

      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const passwordOperation = async (trigger, values, options = {}) => {
    try {
      const response = await trigger(values);
      await handleLoginSuccess(response.token, response.expired);
      if (options.removeParam) removeQueryParam('recoveryCode');
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const createPasswordWithEmail = (values) =>
    passwordOperation(triggers.createPasswordWithEmail, values, {
      removeParam: true,
    });

  const register = (values) => passwordOperation(triggers.register, values);

  const recoveryPassword = (type, values) =>
    resendRequest(triggers[`recoveryPasswordWith${type}`], values);

  const { trigger: smsAuthenticate } = useSWRMutation(
    '/register/phone/confirmation',
    (url, { arg }) =>
      fetcherWithToken(url, {
        method: 'POST',
        body: JSON.stringify(arg),
      })
  );

  return {
    authenticate,
    smsAuthenticate,
    resendSms: (form) => resendRequest(triggers.registerWithPhone, form),
    resendEmail: (form) => resendRequest(triggers.registerWithEmail, form),
    resendRecoveryEmail: (form) =>
      recoveryPassword('Email', { email: form.email }),
    emailCodeAuthenticate,
    recoveryPasswordWithEmail: (values) => recoveryPassword('Email', values),
    recoveryPasswordWithPhone: (values) => recoveryPassword('Phone', values),
    createPasswordWithEmail,
    register,
  };
}

export { useAuthorization };
